import axios, { AxiosError } from 'axios';
import type { User, LoginRequest, LoginResponse, RegisterRequest, UserCreateRequest } from '../types/user';
import type { HealthResponse } from '../types/api';
import type { Task, TaskListItem, TaskCreateRequest, TaskIterationCreateRequest, TaskIteration, TaskFile, TaskTag, TaskTagTreeNode } from '../types/task';
import type { KnowledgeBase, KnowledgeBaseListItem, KBCreateRequest, KBUpdateRequest, KBResourceCreateRequest, KBResource, TaskToKBRequest, KBTag, KBTagTreeNode, KBTagCreateRequest, KBTagUpdateRequest } from '../types/knowledgeBase';
import type { Project, ProjectListItem, ProjectCreateRequest, ProjectUpdateRequest } from '../types/project';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', data);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<User> => {
    const response = await api.post<User>('/auth/register', data);
    return response.data;
  },

  getMe: async (token?: string): Promise<User> => {
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const response = await api.get<User>('/auth/me', config);
    return response.data;
  },

  checkHealth: async (): Promise<HealthResponse> => {
    const response = await api.get<HealthResponse>('/health');
    return response.data;
  },
};

export const usersApi = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/users');
    return response.data;
  },

  create: async (data: UserCreateRequest): Promise<User> => {
    const response = await api.post<User>('/users', data);
    return response.data;
  },

  update: async (id: number, data: Partial<User>): Promise<User> => {
    const response = await api.put<User>(`/users/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  disable: async (id: number): Promise<{success: boolean; message: string}> => {
    const response = await api.post(`/users/${id}/disable`);
    return response.data;
  },

  enable: async (id: number): Promise<{success: boolean; message: string}> => {
    const response = await api.post(`/users/${id}/enable`);
    return response.data;
  },

  resetPassword: async (id: number, newPassword: string): Promise<{success: boolean; message: string}> => {
    const response = await api.post(`/users/${id}/reset-password`, { new_password: newPassword });
    return response.data;
  },
};

export const tasksApi = {
  getAll: async (status?: string, userFilter?: string, showArchived?: boolean, projectId?: number | null): Promise<TaskListItem[]> => {
    const params: { status?: string; user_filter?: string; show_archived?: boolean; project_id?: number } = {};
    if (status && status !== 'archived') params.status = status;
    if (userFilter) params.user_filter = userFilter;
    if (showArchived || status === 'archived') params.show_archived = true;
    if (projectId !== undefined && projectId !== null) params.project_id = projectId;
    const response = await api.get<TaskListItem[]>('/tasks', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Task> => {
    const response = await api.get<Task>(`/tasks/${id}`);
    return response.data;
  },

  create: async (data: TaskCreateRequest): Promise<Task> => {
    const response = await api.post<Task>('/tasks', data);
    return response.data;
  },

  uploadFiles: async (taskId: number, files: File[]): Promise<TaskFile[]> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    const response = await api.post<TaskFile[]>(`/tasks/${taskId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  createIteration: async (taskId: number, data: TaskIterationCreateRequest): Promise<TaskIteration> => {
    const response = await api.post<TaskIteration>(`/tasks/${taskId}/iterations`, data);
    return response.data;
  },

  createIterationWithFiles: async (taskId: number, formData: FormData): Promise<TaskIteration> => {
    const response = await api.post<TaskIteration>(`/tasks/${taskId}/iterations`, formData);
    return response.data;
  },

  uploadIterationFiles: async (taskId: number, iterationId: number, formData: FormData): Promise<void> => {
    await api.post(`/tasks/${taskId}/iterations/${iterationId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  submitIteration: async (taskId: number, iterationId: number): Promise<{success: boolean; message: string}> => {
    const response = await api.post(`/tasks/${taskId}/iterations/${iterationId}/submit`);
    return response.data;
  },

  downloadFile: async (taskId: number, fileId: number): Promise<Blob> => {
    const response = await api.get(`/tasks/${taskId}/files/${fileId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  downloadIterationOutputFiles: async (taskId: number, iterationId: number): Promise<Blob> => {
    const response = await api.get(`/tasks/${taskId}/iterations/${iterationId}/output-files/download-all`, {
      responseType: 'blob',
    });
    return response.data;
  },

  markAsRead: async (taskId: number): Promise<{success: boolean}> => {
    const response = await api.post(`/tasks/${taskId}/mark-read`);
    return response.data;
  },

  getUnreadCount: async (): Promise<{count: number}> => {
    const response = await api.get('/tasks/unread-count');
    return response.data;
  },

  archive: async (taskId: number): Promise<{success: boolean}> => {
    const response = await api.post(`/tasks/${taskId}/archive`);
    return response.data;
  },

  unarchive: async (taskId: number): Promise<{success: boolean}> => {
    const response = await api.post(`/tasks/${taskId}/unarchive`);
    return response.data;
  },

  submitDraft: async (taskId: number): Promise<{success: boolean; message: string}> => {
    const response = await api.post(`/tasks/${taskId}/submit`);
    return response.data;
  },

  delete: async (taskId: number): Promise<{success: boolean; message: string}> => {
    const response = await api.delete(`/tasks/${taskId}`);
    return response.data;
  },

  update: async (taskId: number, data: TaskCreateRequest): Promise<Task> => {
    const response = await api.put<Task>(`/tasks/${taskId}`, data);
    return response.data;
  },

  deleteFile: async (taskId: number, fileId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/files/${fileId}`);
  },

  reorder: async (taskId: number, newOrder: number, newStatus?: string): Promise<{success: boolean; message: string}> => {
    const params: { new_order: number; new_status?: string } = { new_order: newOrder };
    if (newStatus) {
      params.new_status = newStatus;
    }
    const response = await api.post(`/tasks/${taskId}/reorder`, null, { params });
    return response.data;
  },
};

export const knowledgeBasesApi = {
  getAll: async (showArchived: boolean = false): Promise<KnowledgeBaseListItem[]> => {
    const response = await api.get<KnowledgeBaseListItem[]>('/knowledge-bases', {
      params: { show_archived: showArchived }
    });
    return response.data;
  },

  getById: async (id: number): Promise<KnowledgeBase> => {
    const response = await api.get<KnowledgeBase>(`/knowledge-bases/${id}`);
    return response.data;
  },

  create: async (data: KBCreateRequest): Promise<KnowledgeBase> => {
    const response = await api.post<KnowledgeBase>('/knowledge-bases', data);
    return response.data;
  },

  update: async (id: number, data: KBUpdateRequest): Promise<KnowledgeBase> => {
    const response = await api.put<KnowledgeBase>(`/knowledge-bases/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/knowledge-bases/${id}`);
  },

  archive: async (id: number): Promise<{success: boolean}> => {
    const response = await api.post(`/knowledge-bases/${id}/archive`);
    return response.data;
  },

  unarchive: async (id: number): Promise<{success: boolean}> => {
    const response = await api.post(`/knowledge-bases/${id}/unarchive`);
    return response.data;
  },

  uploadFiles: async (kbId: number, files: File[]): Promise<KBResource[]> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    const response = await api.post<KBResource[]>(`/knowledge-bases/${kbId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  addUrl: async (kbId: number, data: KBResourceCreateRequest): Promise<KBResource> => {
    const response = await api.post<KBResource>(`/knowledge-bases/${kbId}/urls`, data);
    return response.data;
  },

  deleteResource: async (kbId: number, resourceId: number): Promise<void> => {
    await api.delete(`/knowledge-bases/${kbId}/resources/${resourceId}`);
  },

  downloadResource: async (kbId: number, resourceId: number): Promise<Blob> => {
    const response = await api.get(`/knowledge-bases/${kbId}/resources/${resourceId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  convertFromTask: async (taskId: number, data: TaskToKBRequest): Promise<KnowledgeBase> => {
    const response = await api.post<KnowledgeBase>(`/tasks/${taskId}/convert-to-kb`, data);
    return response.data;
  },

  setAsDefault: async (id: number): Promise<{success: boolean}> => {
    const response = await api.post(`/knowledge-bases/${id}/set-default`);
    return response.data;
  },

  unsetAsDefault: async (id: number): Promise<{success: boolean}> => {
    const response = await api.post(`/knowledge-bases/${id}/unset-default`);
    return response.data;
  },

  // Tag management for knowledge bases
  addTag: async (kbId: number, tagId: number): Promise<{success: boolean}> => {
    const response = await api.post(`/knowledge-bases/${kbId}/tags/${tagId}`);
    return response.data;
  },

  removeTag: async (kbId: number, tagId: number): Promise<{success: boolean}> => {
    const response = await api.delete(`/knowledge-bases/${kbId}/tags/${tagId}`);
    return response.data;
  },

  getByTag: async (tagId: number, showArchived: boolean = false): Promise<KnowledgeBaseListItem[]> => {
    const response = await api.get<KnowledgeBaseListItem[]>(`/knowledge-bases/by-tag/${tagId}`, {
      params: { show_archived: showArchived }
    });
    return response.data;
  },
};

export const kbTagsApi = {
  getAll: async (): Promise<KBTag[]> => {
    const response = await api.get<KBTag[]>('/kb-tags');
    return response.data;
  },

  getTree: async (): Promise<KBTagTreeNode[]> => {
    const response = await api.get<KBTagTreeNode[]>('/kb-tags/tree');
    return response.data;
  },

  create: async (data: KBTagCreateRequest): Promise<KBTag> => {
    const response = await api.post<KBTag>('/kb-tags', data);
    return response.data;
  },

  update: async (id: number, data: KBTagUpdateRequest): Promise<KBTag> => {
    const response = await api.put<KBTag>(`/kb-tags/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/kb-tags/${id}`);
  },
};

export interface TaskTagCreateRequest {
  name: string;
  parent_id?: number | null;
  color?: string | null;
}

export interface TaskTagUpdateRequest {
  name?: string;
  parent_id?: number | null;
  color?: string | null;
}

export const taskTagsApi = {
  getAll: async (): Promise<TaskTag[]> => {
    const response = await api.get<TaskTag[]>('/task-tags');
    return response.data;
  },

  getTree: async (): Promise<TaskTagTreeNode[]> => {
    const response = await api.get<TaskTagTreeNode[]>('/task-tags/tree');
    return response.data;
  },

  create: async (data: TaskTagCreateRequest): Promise<TaskTag> => {
    const response = await api.post<TaskTag>('/task-tags', data);
    return response.data;
  },

  update: async (id: number, data: TaskTagUpdateRequest): Promise<TaskTag> => {
    const response = await api.put<TaskTag>(`/task-tags/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/task-tags/${id}`);
  },

  // Tag management for tasks
  addTagToTask: async (taskId: number, tagId: number): Promise<{success: boolean}> => {
    const response = await api.post(`/tasks/${taskId}/tags/${tagId}`);
    return response.data;
  },

  removeTagFromTask: async (taskId: number, tagId: number): Promise<{success: boolean}> => {
    const response = await api.delete(`/tasks/${taskId}/tags/${tagId}`);
    return response.data;
  },

  getTasksByTag: async (tagId: number): Promise<TaskListItem[]> => {
    const response = await api.get<TaskListItem[]>(`/tasks/by-tag/${tagId}`);
    return response.data;
  },

  // AI-powered tag suggestions
  suggestTags: async (
    title: string,
    description: string,
    signal?: AbortSignal
  ): Promise<TagSuggestionsResponse> => {
    const response = await api.post<TagSuggestionsResponse>(
      '/task-tags/suggest',
      { title, description },
      { signal }
    );
    return response.data;
  },
};

// Types for auto-tag suggestions
export interface ExistingTagSuggestion {
  tag_id: number;
  tag_name: string;
  tag_path: string;
  color: string | null;
  confidence: number;
  reason: string;
}

export interface NewTagSuggestion {
  name: string;
  parent_id: number | null;
  parent_name: string | null;
  confidence: number;
  reason: string;
}

export interface TagSuggestionsResponse {
  existing_tags: ExistingTagSuggestion[];
  new_tags: NewTagSuggestion[];
}

// Task Enhancement API (AI Assistant)
export interface TaskEnhanceRequest {
  title: string;
  description: string;
}

export interface TaskEnhanceResponse {
  enhanced_title: string;
  enhanced_description: string;
  detected_type: string;
  assumptions: string[];
}

export const taskAssistantApi = {
  enhance: async (
    data: TaskEnhanceRequest,
    signal?: AbortSignal
  ): Promise<TaskEnhanceResponse> => {
    const response = await api.post<TaskEnhanceResponse>(
      '/task-assistant/enhance',
      data,
      { signal }
    );
    return response.data;
  },
};

// Project suggestion types
export interface ExistingProjectSuggestion {
  project_id: number;
  project_name: string;
  task_count: number;
  confidence: number;
  reason: string;
}

export interface NewProjectSuggestion {
  name: string;
  description: string;
  confidence: number;
  reason: string;
}

export interface ProjectSuggestResponse {
  existing_projects: ExistingProjectSuggestion[];
  new_projects: NewProjectSuggestion[];
}

export const projectsApi = {
  getAll: async (showArchived: boolean = false): Promise<ProjectListItem[]> => {
    const response = await api.get<ProjectListItem[]>('/projects', {
      params: { show_archived: showArchived }
    });
    return response.data;
  },

  suggest: async (
    title: string,
    description: string,
    signal?: AbortSignal
  ): Promise<ProjectSuggestResponse> => {
    const response = await api.post<ProjectSuggestResponse>(
      '/projects/suggest',
      { title, description },
      { signal }
    );
    return response.data;
  },

  getById: async (id: number): Promise<Project> => {
    const response = await api.get<Project>(`/projects/${id}`);
    return response.data;
  },

  create: async (data: ProjectCreateRequest): Promise<Project> => {
    const response = await api.post<Project>('/projects', data);
    return response.data;
  },

  update: async (id: number, data: ProjectUpdateRequest): Promise<Project> => {
    const response = await api.put<Project>(`/projects/${id}`, data);
    return response.data;
  },

  archive: async (id: number): Promise<{success: boolean}> => {
    const response = await api.post(`/projects/${id}/archive`);
    return response.data;
  },

  delete: async (id: number): Promise<{success: boolean; message: string}> => {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  },
};

// Project Tag types and API
import type { ProjectTag, ProjectTagTreeNode, ProjectListItem as ProjectListItemType } from '../types/project';

export interface ProjectTagCreateRequest {
  name: string;
  parent_id?: number | null;
  color?: string | null;
}

export interface ProjectTagUpdateRequest {
  name?: string;
  parent_id?: number | null;
  color?: string | null;
}

export const projectTagsApi = {
  getAll: async (): Promise<ProjectTag[]> => {
    const response = await api.get<ProjectTag[]>('/project-tags');
    return response.data;
  },

  getTree: async (): Promise<ProjectTagTreeNode[]> => {
    const response = await api.get<ProjectTagTreeNode[]>('/project-tags/tree');
    return response.data;
  },

  create: async (data: ProjectTagCreateRequest): Promise<ProjectTag> => {
    const response = await api.post<ProjectTag>('/project-tags', data);
    return response.data;
  },

  update: async (id: number, data: ProjectTagUpdateRequest): Promise<ProjectTag> => {
    const response = await api.put<ProjectTag>(`/project-tags/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/project-tags/${id}`);
  },

  // Tag management for projects
  addTagToProject: async (projectId: number, tagId: number): Promise<{success: boolean}> => {
    const response = await api.post(`/projects/${projectId}/tags/${tagId}`);
    return response.data;
  },

  removeTagFromProject: async (projectId: number, tagId: number): Promise<{success: boolean}> => {
    const response = await api.delete(`/projects/${projectId}/tags/${tagId}`);
    return response.data;
  },

  getProjectsByTag: async (tagId: number): Promise<ProjectListItemType[]> => {
    const response = await api.get<ProjectListItemType[]>(`/projects/by-tag/${tagId}`);
    return response.data;
  },
};

export default api;
